import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from "react";
import { CameraLicense, CreateCameraLicense, DeploymentKabaResult, Invoice, KabaUser, NewKabaUserRequest, OptionType } from "../../common/store";
import TextInputComponent from "../shared/TextInputComponent";
import { validate as EmailValidate } from 'email-validator';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RadioComponent from "../shared/RadioComponent";
import API from "../../common/API";
import { useAuth } from "react-oidc-context";
import PaginationListComponent from '../../components/shared/PaginatedListComponent';
import LoadingPageComponent from "../shared/LoadingPageComponent";
import useAppSelector from "../../hooks/useAppSelector";
import { selectConfig } from "../../redux/slices/config";
import Payment from "../../views/Payment/ElavonPayment";
import Collapse from 'react-bootstrap/Collapse';

import appStoreLogoGoogle from '../../assets/images/play-store.png';
import appStoreLogoApple from '../../assets/images/app-store.svg';

export default function KabaLocksetsComponent({deploymentId}:{deploymentId:number}){
    const { t } = useTranslation()

    const auth = useAuth()
    const [loading, setLoading] = useState<boolean>(false)

    const [kabaSiteId, setKabaSiteId] = useState<string|null>(null)
    const [kabaDoorCount, setKabaDoorCount] = useState<number|null>(null)
    const [pendingKabaDoorCount, setPendingKabaDoorCount] = useState<number|null>(null)

    const [section, setSection] = useState<string>("info")
    const [doorsAmount, setDoorsAmount] = useState<number>(0)
    const releaseMode = useAppSelector(selectConfig)

    const [invoice, setInvoice] = useState<Invoice | null >(null)
    const [renewInvoice, setRenewInvoice] = useState<Invoice | null >(null)
    const [prevDoorsAmount, setPrevDoorsAmount] =  useState<number>(0)
    const [kabaConfig, setKabaConfig] = useState<DeploymentKabaResult | null>(null)

    const [kabaUsers, setKabaUsers] = useState<KabaUser[]>([])
    const usersHeader: OptionType[] = [
        { value: "firstName", label: 'First Name' },
        { value: "lastName", label: 'Last Name' },
        { value: "email", label: 'Email' },
    ]

    const [firstName, setFirstName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [email, setEmail] = useState<string>('')

    useEffect(() => {
        getKabaSiteInfo()
    },[])

    const getInvoice = async () => {
        try{
            if (invoice != null && invoice != undefined && prevDoorsAmount == doorsAmount)
            {
                setSection("payment")
                return
            }
            const body:any = {DeploymentId: deploymentId, Amount: doorsAmount}
            setLoading(true)
            const response: Invoice | null | undefined = await API.post(`api/kaba/locksetInvoice`,body, auth)
            if (response != null && response != undefined)
            {
                setInvoice(response)
                setSection("payment")
                setPrevDoorsAmount(doorsAmount)
            }
            setLoading(false)
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setLoading(false)
        }
    }

    const getRenewInvoice = async () => {
        try{
            setLoading(true)
            const response: Invoice | null | undefined = await API.post(`api/kaba/locksetInvoice/renew/${deploymentId}`,{}, auth)
            if (response != null && response != undefined)
            {
                setRenewInvoice(response)
                setSection("renewPayment")
            }
            setLoading(false)
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setLoading(false)
        }
    }

    const getUsers = async () => {
        try {
            setLoading(true)
            const response: KabaUser[] | null | undefined = await API.get(`api/kaba/${deploymentId}/Users`, auth)
            if (response != null && response != undefined) {
                setKabaUsers(response)
            }
            setLoading(false)
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setLoading(false)
        }
    }

    const AddKabaUser = async () => {
        const object: NewKabaUserRequest = {
            firstName: firstName,
            lastName: lastName,
            email: email
        }
        try {
            setLoading(true)
            const user = await API.post(`api/kaba/${deploymentId}/CreateUser`, object, auth) as KabaUser
            setKabaUsers(kabaUsers => [...kabaUsers, user])
            clearUserForm()
            setLoading(false)
            setSection('info')
        }
        catch (e: any) {
            alert(e)
            setLoading(false)
        }
    }

    const clearUserForm = async () => {
        setFirstName('')
        setLastName('')
        setEmail('')
    }

    const decreateDoorCount = async () => {
        try{
            const body:any = {DeploymentId: deploymentId, Amount: doorsAmount}
            setLoading(true)
            const response: any = await API.put(`api/kaba/decreasePendingLocksetAmount`,body, auth)
            getKabaSiteInfo()
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setLoading(false)
        }
    }

    const undoPending = async () => {
        try{
            setLoading(true)
            const response: any = await API.put(`api/kaba/undoPendingLocksetAmount/${deploymentId}`,{}, auth)
            getKabaSiteInfo()
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setLoading(false)
        }
    }

    const getKabaSiteInfo = async () => {
        try {
            setLoading(true)
            setKabaDoorCount(null)
            setKabaSiteId(null)
            const response: DeploymentKabaResult | null = await API.get(`api/kaba/${deploymentId}`, auth)
            if (response != null)
            {
                setKabaSiteId(response.kabaSiteId!)
                setKabaDoorCount(response.kabaDoorCount!)
                setPendingKabaDoorCount(response.pendingKabaDoorCount!)
                setKabaConfig(response)
                if (response.kabaSiteId != null)
                    getUsers()
            }
            setLoading(false)
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setLoading(false)
        }
    }

    const activateKabaSite = async () => {
        try{
            setLoading(true)
            const response: DeploymentKabaResult | null = await API.post(`api/kaba/createSite/${deploymentId}`,{}, auth)
            if (response != null)
            {
                setKabaSiteId(response.kabaSiteId!)
                setKabaDoorCount(response.kabaDoorCount!)
                setSection('tutorial')
            }
            setLoading(false)
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setLoading(false)
        }
    }

    const forceSync = async () => {
        try{
            setLoading(true)
            const response: any = await API.post(`api/kaba/forceSyncKabaAllow/${deploymentId}`,{}, auth)
            setLoading(false)
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setLoading(false)
        }
    }

    const PostFunction = async (isinvoice?: boolean) => {
        try{
            const body:any = {InvoiceNumber: invoice?.invoiceNumber, IsInvoiced: isinvoice}
            setLoading(true)
            const response: Invoice | null | undefined = await API.put(`api/kaba/locksetPaid`,body, auth)
            setSection("info")
            getKabaSiteInfo()
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setSection("info")
            setLoading(false)
        }
    }

    const PostRenewFunction = async (isinvoice?: boolean) => {
        try{
            const body:any = {InvoiceNumber: renewInvoice?.invoiceNumber, IsInvoiced: isinvoice}
            setLoading(true)
            const response: Invoice | null | undefined = await API.put(`api/kaba/locksetPaid/true`,body, auth)
            setSection("info")
            getKabaSiteInfo()
        }
        catch (e: any) {
            alert(e.errors?.Error!)
            setSection("info")
            setLoading(false)
        }
    }

    const [openTutorialUser, setOpenTutorialUser] = useState(false);
    const [openTutorialEmail, setOpenTutorialEmail] = useState(false);
    const [openTutorialLicense, setOpenTutorialLicense] = useState(false);
    const [openTutorialReset, setOpenTutorialReset] = useState(false);
    const [openTutorialCommission, setOpenTutorialCommission] = useState(false);
    const [openTutorialOdyssey, setOpenTutorialOdyssey] = useState(false);

    return (
        <div>
            {section === "info" && 
                <div>
                    <div className="card">
                        <div className="card-header">
                            <h3 className='mb-4 header-text '>
                                <FontAwesomeIcon icon="door-open" className="fs-3 me-2" />
                                <span>{`${t('kaba:dormakabaSaffireEVO')}`}</span>
                                {
                                    kabaSiteId != null && loading === false && <div>
                                        <button className="btn btn-success float-end" onClick={() => forceSync()}>
                                            <span>{`${t('kaba:forceSystemSync')}`}</span>
                                        </button>
                                        <button className="btn btn-secondary float-end me-2" onClick={() => setSection('tutorial') }>
                                            <span>{`${t('kaba:instructions:nextSteps')}`}</span>
                                        </button>
                                    </div>
                                }
                            </h3>
                        </div>
                        <div className="card-body row mb-3">
                            {
                                kabaSiteId == null && loading == false && 
                                <div className="text-center">
                                    <button className="btn btn-success w-100" style={{height:'50px'}} onClick={() => activateKabaSite()}>
                                        {`${t('kaba:activateSaffireEvo')}`}
                                    </button>
                                </div>
                            }
                            {
                                kabaSiteId != null && loading == false && kabaConfig != null && 
                                <div>
                                    <TextInputComponent value={kabaSiteId} label={t('kaba:kabaSiteId')}  noEdit />
                                    <TextInputComponent value={kabaDoorCount?.toString()!} label={t('kaba:kabaDoorCount')} noEdit />
                                    { pendingKabaDoorCount != null && pendingKabaDoorCount != undefined &&  
                                    <div className="form-group row ">
                                        <div className="col-1 d-none d-sm-block"></div>
                                        <label className="col-4 col-sm-2 col-form-label text-begin mb-4 ms-3">
                                            <h3 className="d-none d-sm-block  text-warning">{`${t('kaba:pendingCount')}`}</h3>
                                            <h3 className="d-block d-sm-none fs-4  text-warning">{`${t('kaba:pendingCount')}`}</h3>
                                        </label>
                                        <div className="col-7 col-sm-5">
                                            <span className=" form-control bg-white fw-bolder fs-5 pt-0 mt-1 border-0 text-warning d-inline">{pendingKabaDoorCount!}</span>
                                            <button className="btn btn-sm btn-secondary ms-4" onClick={() => undoPending()}>
                                                {`${t('deployment:undo')}`}
                                            </button>
                                        </div>
                                    </div>
                                    }
                                    {(kabaConfig.renewalDate != null) && <TextInputComponent value={new Date(kabaConfig.renewalDate ?.toString()!).toDateString()!} label={t("deployment:renewalDate")} noEdit />}
                                    {(kabaConfig.pastDueGraceDate != null) && <TextInputComponent value={new Date(kabaConfig.pastDueGraceDate ?.toString()!).toDateString()!} label={t("kaba:pastDueGraceDate")} noEdit />}
                                    {kabaConfig!.pastDue == false && <div className="form-group row">
                                        <div className="col-1 d-none d-sm-block"></div>
                                        <label className="col-4 col-sm-2 col-form-label text-begin mb-4 ms-3">
                                            <h3 className="d-none d-sm-block ">{`${t('kaba:updateLocksets')}`}</h3>
                                            <h3 className="d-block d-sm-none fs-4 ">{`${t('kaba:updateLocksets')}`} </h3>
                                        </label>
                                        <div className="col-4">
                                            <div className="text-center  ms-auto me-auto">
                                                <input className="align-bottom form-control  " min={1} type="number" id="flexSwitchCheckDefault" value={doorsAmount} onChange={(e) => setDoorsAmount(Number(e.target.value))} />
                                            </div>
                                        </div>
                                        <div className="col-1 col-xxl-2">
                                            <div className="text-center">
                                                <button className="btn btn-sm btn-bitbucket " disabled={doorsAmount == 0} onClick={() => getInvoice()} >
                                                    <FontAwesomeIcon icon="plus" className="fs-5" />
                                                </button>
                                                <button className="btn btn-sm btn-pinterest add-btn-text ms-xxl-2 mt-2 mt-xxl-0 " disabled={doorsAmount > kabaDoorCount !|| doorsAmount == 0 } onClick={() => decreateDoorCount()} >
                                                    <FontAwesomeIcon icon="minus" className="fs-5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>}
                                    {kabaConfig!.pastDue == true &&
                                        <button className="btn btn-primary float-end" onClick={() => getRenewInvoice()}>
                                            {`${t('license:renew')}`}
                                        </button>
                                    }
                                </div>
                            }
                            {
                                loading == true &&
                                <LoadingPageComponent />
                            }
                        </div>
                    </div>
                    {kabaSiteId != null && <div className="card">
                        {!loading && <div>
                            <div className="card-header">
                                <h3 className="mb-0 header-text">
                                    <FontAwesomeIcon icon="user" className="fs-3 me-2" />
                                    <span>{`${t('kaba:dormakabaSaffireEVO')}`} {`${t('common:users')}`}</span>
                                    <button className="btn btn-primary float-end" onClick={() => setSection("userInvite")}>
                                        <span>Invite User</span>
                                    </button>
                                </h3>
                                <p className="p-0">Users are required to commission locksets within the Lyazon App.</p>
                            </div>
                            {kabaUsers != null && <div className="card-body row mb-3">
                                <PaginationListComponent
                                    isEditable={false}
                                    isRemovable={true}
                                    list={kabaUsers!}
                                    headerValues={usersHeader}
                                    repopulate={getUsers}
                                    deleteString={`api/kaba/${deploymentId}/DeleteUser/`}
                                    label={t('user')} />
                            </div>}
                        </div>}
                        {
                            loading == true &&
                            <LoadingPageComponent />
                        }
                    </div>}
                </div>
            }
            {section === "userInvite" &&
                <div className="card">
                    <div className=" card-header border-0 ">
                        <h3 className='header-text'><FontAwesomeIcon icon="user" className="fs-3 me-2" />Invite User</h3>
                        <p>The user will receive an email to setup their Dormakaba Lyazon App account.</p>
                    </div>
                    <div className="card-body mb-3">

                        {
                            loading == true &&
                            <LoadingPageComponent />
                        }
                        {!loading && <div>
                            <TextInputComponent mandatory value={firstName} label={t('user:firstName')} setText={setFirstName} />
                            <TextInputComponent mandatory value={lastName} label={t('user:lastName')} setText={setLastName} />
                            <TextInputComponent mandatory value={email} label={t('common:email')} setText={setEmail} validator={EmailValidate} validateText={t('user:emailValidationError')} />
                            <button className="btn btn-secondary " onClick={() => setSection("info")} >
                                <span>{`${t('previous')}`}</span>
                            </button>

                            <button className="mb-4 btn float-end btn-primary add-btn-text " disabled={firstName === '' || lastName === '' || !EmailValidate(email)}
                                onClick={() => { AddKabaUser() }}>
                                <span>{`${t('kaba:sendInvitation')}`}</span>
                            </button>
                        </div>
                        }
                    </div>
                </div>
            }
            {section === "tutorial" &&
                <div className="card">
                    <div className=" card-header border-0 ">
                        <h2 className='header-text mb-3'><FontAwesomeIcon icon="door-open" className="fs-3 me-2" />{`${t('kaba:instructions:nextSteps')}`}</h2>
                    </div>
                    <div className="card-body mb-3">
                        <div className="row">
                            <div className="col-xl-10">

                                <div>
                                    <div className="border-bottom mb-3">
                                        <h3 className="" onClick={() => setOpenTutorialUser(!openTutorialUser)}>
                                            <FontAwesomeIcon icon="chevron-right" className="me-2 text-muted" />
                                            <span>{`${t('kaba:instructions:userManagement')}`}</span>
                                        </h3>
                                    </div>
                                    <Collapse in={openTutorialUser}>
                                        <p>{`${t('kaba:instructions:userManagementDescription')}`}</p>
                                    </Collapse>
                                </div>

                                <div>
                                    <div className="border-bottom mb-3">
                                        <h3 className="" onClick={() => setOpenTutorialEmail(!openTutorialEmail)}>
                                            <FontAwesomeIcon icon="chevron-right" className="me-2 text-muted" />
                                            <span>{`${t('kaba:instructions:emailInvitation')}`}</span>
                                        </h3>
                                    </div>
                                    <Collapse in={openTutorialEmail}>
                                        <p>{`${t('kaba:instructions:emailInvitationDescription')}`}</p>
                                    </Collapse>
                                </div>

                                <div>
                                    <div className="border-bottom mb-3">
                                        <h3 className="" onClick={() => setOpenTutorialLicense(!openTutorialLicense)}>
                                            <FontAwesomeIcon icon="chevron-right" className="me-2 text-muted" />
                                            <span>{`${t('kaba:instructions:locksetLicense')}`}</span>
                                        </h3>
                                    </div>
                                    <Collapse in={openTutorialLicense}>
                                        <p>{`${t('kaba:instructions:locksetLicenseDescription')}`}</p>
                                    </Collapse>
                                </div>

                                <div>
                                    <div className="border-bottom mb-3">
                                        <h3 className="" onClick={() => setOpenTutorialReset(!openTutorialReset)}>
                                            <FontAwesomeIcon icon="chevron-right" className="me-2 text-muted" />
                                            <span>{`${t('kaba:instructions:factoryReset')}`}</span>
                                        </h3>
                                    </div>
                                    <Collapse in={openTutorialReset}>
                                        <div>
                                            <p>{`${t('kaba:instructions:factoryResetDescription')}`}</p>

                                            <ol>
                                                <li>{`${t('kaba:instructions:factoryResetSteps:1')}`}</li>
                                                <li>{`${t('kaba:instructions:factoryResetSteps:2')}`}</li>
                                                <li>{`${t('kaba:instructions:factoryResetSteps:3')}`}</li>
                                                <li>{`${t('kaba:instructions:factoryResetSteps:4')}`}</li>
                                                <li>{`${t('kaba:instructions:factoryResetSteps:5')}`}</li>
                                            </ol>
                                        </div>
                                    </Collapse>
                                </div>

                                <div>
                                    <div className="border-bottom mb-3">
                                        <h3 className="" onClick={() => setOpenTutorialCommission(!openTutorialCommission)}>
                                            <FontAwesomeIcon icon="chevron-right" className="me-2 text-muted" />
                                            <span>{`${t('kaba:instructions:commissionLockset')}`}</span>
                                        </h3>
                                    </div>
                                    <Collapse in={openTutorialCommission}>
                                        <div>
                                            <p>{`${t('kaba:instructions:commissionLocksetDescription')}`}</p>

                                            <div className="text-center mb-4">
                                                <a target="_blank" rel="noreferrer" className="m-2" href="https://play.google.com/store/apps/details?id=com.dormakaba.lyazon&pcampaignid=web_share">
                                                    <img height="40" alt='Get it on Google Play' src={appStoreLogoGoogle} />
                                                </a>

                                                <a target="_blank" rel="noreferrer" className="m-2" href="https://apps.apple.com/us/app/dormakaba-lyazon-utility/id6444566137">
                                                    <img height="40" alt='Get it on Google Play' src={appStoreLogoApple} />
                                                </a>
                                            </div>
                                            
                                            <ol>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:1')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:2')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:3')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:4')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:5')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:6')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:7')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:8')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:9')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:10')}`}</li>
                                                <li>{`${t('kaba:instructions:commissionLocksetSteps:11')}`}</li>
                                            </ol>
                                            <p>{`${t('kaba:instructions:commissionLocksetSteps:12')}`}</p>
                                        </div>
                                    </Collapse>
                                </div>

                                <div>
                                    <div className="border-bottom mb-3">
                                        <h3 className="" onClick={() => setOpenTutorialOdyssey(!openTutorialOdyssey)}>
                                            <FontAwesomeIcon icon="chevron-right" className="me-2 text-muted" />
                                            <span>{`${t('kaba:instructions:odyssey')}`}</span>
                                        </h3>
                                    </div>
                                    <Collapse in={openTutorialOdyssey}>
                                        <div>
                                            <p>{`${t('kaba:instructions:odysseyDescription')}`}</p>
                                        </div>
                                    </Collapse>
                                </div>
                                
                            </div>
                        </div>
                        <button className="btn btn-secondary " onClick={() => setSection("info")} >
                            <span>Continue</span>
                        </button>
                    </div>
                </div>
            }
            {section === "payment" &&
                <div className=" mb-4 ms-auto me-auto card shadow border-2" >
                    <div className=" card-header border-0 ">
                        <h2 className='mb-4 header-text'><FontAwesomeIcon icon="credit-card" className="fs-3 me-2" />{`${t('payment')}`}</h2>
                    </div>
                    <div className="card-body bg-opacity-75 ">
                        <Payment invoice={invoice} postFunction={PostFunction} licensePayment={true}  />
                        <button className="btn  btn-secondary " onClick={() => setSection("info")} >
                            <span>{`${t('previous')}`}</span>
                        </button>
                    </div>
                </div>
            }
            {section === "renewPayment" &&
                <div className=" mb-4 ms-auto me-auto card shadow border-2" >
                    <div className=" card-header border-0 ">
                        <h2 className='mb-4 header-text'><FontAwesomeIcon icon="credit-card" className="fs-3 me-2" />{`${t('payment')}`}</h2>
                    </div>
                    <div className="card-body bg-opacity-75 ">
                        <Payment invoice={renewInvoice} postFunction={PostRenewFunction} licensePayment={true}  />
                        <button className="btn  btn-secondary " onClick={() => { setSection("info") }}>
                            <span>{`${t('previous')}`}</span>
                        </button>
                    </div>
                </div>
            }
        </div>
    )
}